import React, { PropsWithChildren, useMemo, useState } from 'react';
import { T } from 'src/shared/helpers';
import styled, { useTheme } from 'styled-components';
import { GhostButton } from '../buttons';
import { DropdownActionItem, DropdownActions } from '../dropdown';
import { Icon } from '../icon';
import { SidebarButton, SidebarItem, SidebarItemIdType } from '../layouts';
import { ZTooltip } from '../ztooltip';
import { CollapsibleTreeItems, SidebarTreeItem } from './collapsibleTreeItems';
import {
    SidebarButtons,
    SidebarContainer,
    SidebarHeader,
    SidebarHeaderContainer,
    SidebarListItem,
    SidebarSearchInput,
    StyledSpan,
    TooltipCategories
} from './commonStyleSideBar';
import MaterialCloseSvg from 'src/shared/material_icons/navigation/close.svg';
import MaterialSearchSvg from 'src/shared/material_icons/action/search.svg';
import MoreVertIcon from 'src/shared/material_icons/navigation/more_vert.svg';
export const TreeSidebarIcon = styled(Icon)<{ $isActive?: boolean }>`
    ${(props) =>
        props.$isActive &&
        `
        color: ${props.theme.app.secondary};
    `}
`;

const TreeSidebarHeaderActionIcon = styled(Icon)``;

const DropdownActionItemContent = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const DropdownActionItemIcon = styled(Icon)<{ $isActive?: boolean }>`
    width: 14px;
    height: 14px;
    flex-shrink: 0;

    ${(props) =>
        props.$isActive &&
        `
        color: ${props.theme.app.secondary};
    `}
`;

interface SidebarProps<T extends SidebarItemIdType = string> {
    sidebarHeader?: string;
    sidebarHeaderButtons?: SidebarButton[];
    // Pulsanti header resi DOPO il toggle di ricerca (es. una freccia di collasso che deve restare ultima a destra).
    sidebarHeaderTrailingButtons?: SidebarButton[];
    sidebarAllowSearch?: boolean;
    isDialog?: boolean;
    showTooltip?: boolean;
    className?: string;
    sidebarItems: SidebarItem<T>[];
    maxWidthItems?: string;
    onSidebarItemClick: (item: SidebarItem<T>) => void;
    onSidebarItemButtonClick?: (button: SidebarButton, item: SidebarItem<T>) => void;
    onSidebarHeaderButtonClick?: (button: SidebarButton) => void;
    onSidebarSubItemButtonClick?: (button: SidebarButton, item: SidebarItem<T>) => void;
    footer?: React.ReactNode;
}

export const TreeSidebar = <T extends SidebarItemIdType = string>({
    sidebarHeader,
    sidebarAllowSearch,
    sidebarHeaderButtons,
    sidebarHeaderTrailingButtons,
    sidebarItems,
    isDialog,
    showTooltip = true,
    className,
    maxWidthItems,
    onSidebarItemClick,
    onSidebarItemButtonClick,
    onSidebarSubItemButtonClick,
    onSidebarHeaderButtonClick,
    footer
}: PropsWithChildren<SidebarProps<T>>) => {
    const [isSearchMode, setSearchMode] = useState(false);
    const [filter, setFilter] = useState('');

    const theme = useTheme();

    const items = useMemo(
        () =>
            sidebarItems.filter(
                (x) =>
                    x.label.toLowerCase().indexOf(filter.toLowerCase()) !== -1 ||
                    (x.subItems != undefined && x.subItems.some((category) => category.label.toLowerCase().indexOf(filter.toLowerCase()) !== -1))
            ),
        [filter, sidebarItems]
    );

    const renderSingleButton = (button: SidebarButton, item: SidebarItem<T>, onClick?: (button: SidebarButton, item: SidebarItem<T>) => void) => {
        const buttonElement = (
            <GhostButton
                $size='small'
                $type='icon'
                key={button.key}
                onClick={(e) => {
                    e.stopPropagation();
                    onClick?.(button, item);
                }}
            >
                <TreeSidebarIcon $isActive={button.isActive}>{button.icon}</TreeSidebarIcon>
            </GhostButton>
        );

        return button.tooltip ? (
            <ZTooltip key={button.key} position='top' title={button.tooltip}>
                {buttonElement}
            </ZTooltip>
        ) : (
            buttonElement
        );
    };

    const renderButtons = (buttons: SidebarButton[], item: SidebarItem<T>, onClick?: (button: SidebarButton, item: SidebarItem<T>) => void) => {
        if (!buttons || buttons.length === 0) return null;

        if (buttons.length === 1) return renderSingleButton(buttons[0], item, onClick);

        return (
            <DropdownActions
                header={
                    <GhostButton $size='small' $type='icon' aria-label={T._('Actions', 'Admin')}>
                        <Icon>
                            <MoreVertIcon />
                        </Icon>
                    </GhostButton>
                }
                items={buttons.map((button) => (
                    <DropdownActionItem key={button.key} onClick={() => onClick?.(button, item)}>
                        <DropdownActionItemContent>
                            <DropdownActionItemIcon $isActive={button.isActive}>{button.icon}</DropdownActionItemIcon>
                            {button.tooltip}
                        </DropdownActionItemContent>
                    </DropdownActionItem>
                ))}
                orientation='RIGHT'
            />
        );
    };

    const renderHeaderButton = (button: SidebarButton) => {
        const buttonElement = (
            <GhostButton $size='small' $type='icon' key={button.key} onClick={() => onSidebarHeaderButtonClick?.(button)}>
                {button.icon}
            </GhostButton>
        );

        return button.tooltip ? (
            <span key={button.key} title={button.tooltip}>
                {buttonElement}
            </span>
        ) : (
            buttonElement
        );
    };

    const macrocategories: SidebarTreeItem[] = items.map((x) => {
        const subCategories: SidebarTreeItem[] =
            x.subItems == null
                ? []
                : x.subItems
                      .filter((x) => x.label.toLowerCase().indexOf(filter) !== -1)
                      .map((x) => {
                          let categoryNode = (
                              <SidebarListItem key={x.id.toString()} $selected={x.selected} disabled={x.disabled} onClick={() => onSidebarItemClick(x)}>
                                  {showTooltip ? (
                                      <TooltipCategories position={'top'} title={x.label}>
                                          <StyledSpan maxWidth={maxWidthItems}>{x.label}</StyledSpan>
                                      </TooltipCategories>
                                  ) : (
                                      <StyledSpan maxWidth={maxWidthItems}>{x.label}</StyledSpan>
                                  )}
                                  <SidebarButtons>{x.buttons && renderButtons(x.buttons, x, onSidebarSubItemButtonClick)}</SidebarButtons>
                              </SidebarListItem>
                          );

                          return {
                              id: x?.id,
                              content: categoryNode
                          };
                      });
        const macroNode = (
            <SidebarListItem
                $selected={x.selected}
                disabled={x.disabled}
                //only for 'All Categories'
                onClick={() => onSidebarItemClick(x)}
            >
                {showTooltip ? (
                    <TooltipCategories title={x.label}>
                        <StyledSpan maxWidth={maxWidthItems}>{x.label}</StyledSpan>
                    </TooltipCategories>
                ) : (
                    <StyledSpan maxWidth={maxWidthItems}>{x.label}</StyledSpan>
                )}
                <SidebarButtons>{x.buttons && renderButtons(x.buttons, x, onSidebarItemButtonClick)}</SidebarButtons>
            </SidebarListItem>
        );

        return {
            id: x?.id,
            content: macroNode,
            items: subCategories,
            opened: x?.subItems?.some((x) => x?.selected)
        };
    });
    return (
        <SidebarContainer
            $isDialog={isDialog ?? false}
            $isMainSidebar={theme?.additionalProperties.isSidebarHidden ?? false}
            position={'sticky'}
            className={className}
        >
            {sidebarHeader && (
                <SidebarHeaderContainer>
                    {!isSearchMode && (
                        <>
                            <SidebarHeader>{sidebarHeader}</SidebarHeader>
                            <SidebarButtons>
                                {sidebarHeaderButtons && sidebarHeaderButtons.map(renderHeaderButton)}

                                {sidebarAllowSearch && (
                                    <GhostButton $size='small' $type='icon' key={'search'} onClick={() => setSearchMode(!isSearchMode)}>
                                        <TreeSidebarHeaderActionIcon>
                                            <MaterialSearchSvg />
                                        </TreeSidebarHeaderActionIcon>
                                    </GhostButton>
                                )}

                                {sidebarHeaderTrailingButtons && sidebarHeaderTrailingButtons.map(renderHeaderButton)}
                            </SidebarButtons>
                        </>
                    )}

                    {isSearchMode && (
                        <>
                            <SidebarSearchInput
                                ref={(instance) => instance?.focus()}
                                containerStyle={{ flex: '1', marginRight: '10px' }}
                                defaultValue={''}
                                onSearch={setFilter}
                                placeholder={T._('Search...', 'Admin')}
                            />

                            <SidebarButtons>
                                <GhostButton
                                    $size='small'
                                    $type='icon'
                                    onClick={() => {
                                        setSearchMode(false);
                                        setFilter('');
                                    }}
                                >
                                    <TreeSidebarHeaderActionIcon>
                                        <MaterialCloseSvg />
                                    </TreeSidebarHeaderActionIcon>
                                </GhostButton>
                            </SidebarButtons>
                        </>
                    )}
                </SidebarHeaderContainer>
            )}
            <CollapsibleTreeItems items={macrocategories} />
            {footer}
        </SidebarContainer>
    );
};
